import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// КАРУСЕЛЬ «НАШИ ТУРИСТЫ В ЯПОНИИ».
//
// Живые групповые кадры из поездок — с флагом Kazakh Japan у Фудзи, в Асакусе,
// у Будды в Камакуре. Это не отзывы из админки, а постоянный набор фото,
// поэтому файлы лежат в public/tourists, а список — прямо здесь.
//
// Чтобы добавить фото: положить JPG (~1600 px по длинной стороне, до 500 КБ)
// в public/tourists и дописать строку в TOURIST_PHOTOS с его шириной и
// высотой — по ним браузер заранее резервирует место под кадр.
//
// Кадры вперемешку вертикальные и горизонтальные, а на групповом фото люди
// стоят у самого края — обрезать нельзя. Поэтому у ленты общая высота, а
// ширина каждой карточки своя, по пропорциям снимка.
const TOURIST_PHOTOS = [
  ["tourist-14.jpg", 1600, 900],
  ["tourist-08.jpg", 900, 1600],
  ["tourist-03.jpg", 1600, 1067],
  ["tourist-10.jpg", 1600, 1200],
  ["tourist-06.jpg", 1200, 1600],
  ["tourist-11.jpg", 1600, 900],
  ["tourist-02.jpg", 1067, 1600],
  ["tourist-15.jpg", 1600, 900],
  ["tourist-05.jpg", 900, 1600],
  ["tourist-04.jpg", 1600, 1066],
  ["tourist-16.jpg", 900, 1600],
  ["tourist-13.jpg", 1200, 1600],
  ["tourist-01.jpg", 1600, 1200],
  ["tourist-09.jpg", 900, 1600],
  ["tourist-12.jpg", 960, 1280],
  ["tourist-07.jpg", 1200, 1600],
].map(([file, width, height]) => ({ src: `/tourists/${file}`, width, height }));

export default function TouristsCarousel({ className = "" }) {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(null);
  const [paused, setPaused] = useState(false);

  const count = TOURIST_PHOTOS.length;

  const scrollTo = useCallback((i) => {
    const track = trackRef.current;
    const slide = track?.children?.[i];
    if (!track || !slide) return;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, []);

  const go = useCallback(
    (next) => {
      const i = (next + count) % count;
      setIndex(i);
      scrollTo(i);
    },
    [count, scrollTo]
  );

  // Автопрокрутка — по тем же правилам, что у карусели отзывов: пауза при
  // наведении, фокусе и открытом кадре, выключена при reduced motion.
  useEffect(() => {
    if (paused || zoomed !== null) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => go(index + 1), 4500);
    return () => clearInterval(id);
  }, [paused, zoomed, index, go]);

  // В полноэкранном режиме стрелки клавиатуры листают кадры, Esc закрывает.
  useEffect(() => {
    if (zoomed === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomed(null);
      if (e.key === "ArrowRight") setZoomed((z) => (z + 1) % count);
      if (e.key === "ArrowLeft") setZoomed((z) => (z - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed, count]);

  const arrow =
    "h-10 w-10 rounded-full border border-kj-line text-kj-ink flex items-center justify-center hover:border-kj-gold hover:text-kj-gold transition-colors";

  return (
    <section
      aria-label={t("reviews.touristsTitle")}
      className={`w-full ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
          <span className="underline underline-offset-[6px] decoration-kj-gold decoration-2">
            {t("reviews.touristsTitle")}
          </span>
        </h2>

        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label={t("reviews.prevPhoto")}
            className={arrow}
          >
            <ChevronLeft aria-hidden="true" className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label={t("reviews.nextPhoto")}
            className={arrow}
          >
            <ChevronRight aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      </div>

      <ul
        ref={trackRef}
        className="no-scrollbar mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
      >
        {TOURIST_PHOTOS.map((photo, i) => (
          <li key={photo.src} className="snap-start flex-shrink-0">
            <button
              type="button"
              onClick={() => setZoomed(i)}
              aria-label={t("reviews.openTouristPhoto")}
              className="block rounded-2xl overflow-hidden border border-kj-line bg-kj-card/60 hover:border-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold transition-colors"
            >
              <img
                src={photo.src}
                alt={t("reviews.touristPhotoAlt")}
                width={photo.width}
                height={photo.height}
                loading="lazy"
                decoding="async"
                className="block h-64 sm:h-80 lg:h-96 w-auto max-w-[85vw] object-cover"
              />
            </button>
          </li>
        ))}
      </ul>

      {zoomed !== null && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomed(null)}
          role="presentation"
        >
          <button
            type="button"
            onClick={() => setZoomed(null)}
            aria-label={t("reviews.closePhoto")}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((zoomed - 1 + count) % count);
            }}
            aria-label={t("reviews.prevPhoto")}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <ChevronLeft aria-hidden="true" className="h-5 w-5" />
          </button>
          <img
            src={TOURIST_PHOTOS[zoomed].src}
            alt={t("reviews.touristPhotoAlt")}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain rounded-lg"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((zoomed + 1) % count);
            }}
            aria-label={t("reviews.nextPhoto")}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <ChevronRight aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      )}
    </section>
  );
}
